/**
 * หน้าจอกฎตรวจรับเข้า (Incoming Inspection Rule, P2-3a) — รายการ ค้นหา สร้าง แก้ไข
 *
 * ปิดทั้งสี่เส้นทางของ contract ที่ `WO-P2-M3-001` เปิดไว้ (D-086):
 * `GET`/`POST /v1/product-supplier-quality-rules` และ `GET`/`PATCH .../{rule_id}`
 *
 * หน้าจอนี้ตอบคำถามเดียวให้ทั้งโรงงาน: **ของที่รับเข้าจากผู้ขายรายนี้ สำหรับสินค้า
 * ตัวนี้ ต้องกักรอตรวจหรือไม่** — งานรับเข้า (P2-3b) เป็นผู้อ่านคำตอบนั้นตอนของมาถึง
 * จริง สิ่งที่ถูกตั้งที่นี่จึงเปลี่ยนว่าของล็อตหน้าจะเดินเข้าคลังได้เลยหรือถูกกักไว้
 *
 * โครงของหน้าจอยืมมาจาก `suppliers/SupplierScreen` ทั้งท่อนโดยตั้งใจ — ช่องค้นหาอยู่
 * บนสุด ฟอร์มสร้างอยู่หลังปุ่มในกล่อง `Modal` และหลังบันทึกสำเร็จก็ **โหลดรายการใหม่
 * ผ่าน client เส้นเดิม** ไม่ใช่รีโหลดทั้งหน้า
 *
 * สี่ข้อที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้:
 *
 * 1. **ไม่มีโรงงานที่ไหนเลย** ทั้งคอลัมน์ ตัวกรอง และช่องกรอก — กฎนี้เป็นกฎระดับ
 *    องค์กร (D-086) ตัวเลือกโรงงานบนหน้าจอนี้จะบอกผู้ใช้ผิดว่าเขากำลังตั้งกฎให้โรงงาน
 *    เดียว ทั้งที่มันมีผลกับทุกโรงงาน
 * 2. **ธงการตรวจไม่มีค่าเริ่มต้นในกล่องสร้าง** ระบบ default เป็น "ต้องตรวจ" อยู่แล้ว
 *    เมื่อไม่มีกฎ กฎที่เขียนลงไปโดยไม่ได้ตัดสินใจจึงไม่ได้บอกอะไรเพิ่ม และคนเขียนอาจ
 *    เข้าใจผิดว่าได้ยกเว้นการตรวจไปแล้ว
 * 3. **คู่ (สินค้า, ผู้ขาย) แก้ไม่ได้** กล่องแก้ไขจึงแสดงคู่นี้เป็นข้อความอ่านอย่าง
 *    เดียว ไม่ใช่ช่องกรอกที่ disabled ไว้ — คำขอที่มีคีย์ทั้งสองติดไปด้วยจะถูกตอบ
 *    `400 QUALITY_RULE_PAIR_IS_IMMUTABLE`
 * 4. **ไม่มีการลบกฎ** ยกเลิกการตรวจคือการเปลี่ยนธงเป็น "ไม่ต้องตรวจ" ซึ่งมีผู้กระทำ
 *    และมีแถว audit ส่วนการลบแถวทิ้งจะทำให้ระบบเงียบ ๆ กลับไปใช้ค่าเริ่มต้น
 *    "ต้องตรวจ" โดยไม่มีใครรู้ว่าใครเป็นคนเปลี่ยน
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับทุกหน้าจอธุรกิจ: `INVENTORY` + `VIEW` เป็นสิทธิ์ของ
 * **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route) ส่วน `CREATE` และ `EDIT` เป็นสิทธิ์ของ
 * **ปุ่มที่เขียน** ซึ่งถูกบังคับข้างในนี้ทีละปุ่ม
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request
 * เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createQualityRule,
  fetchQualityRules,
  ruleFromFailure,
  updateQualityRule,
  type ApiErrorEnvelope,
  type QualityRuleFailure,
} from '../../lib/quality/client';
import {
  INSPECTION_CHOICE_DESCRIPTORS,
  INSPECTION_UNDECIDED,
  filterQualityRules,
  flagToInspectionChoice,
  incomingInspectionLabel,
  incomingInspectionTone,
  toCreateQualityRuleBody,
  toUpdateQualityRuleBody,
} from '../../lib/quality/qualityRulesView';
import {
  INVENTORY_MODULE,
  PRODUCT_NOT_FOUND,
  QUALITY_RULE_EXISTS,
  QUALITY_RULE_FIELD_IS_SERVER_OWNED,
  QUALITY_RULE_NOTE_MAX_LENGTH,
  QUALITY_RULE_PAIR_IS_IMMUTABLE,
  SUPPLIER_NOT_FOUND,
  type ProductSupplierQualityRule,
} from '../../lib/quality/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type QualityFailureContext =
  /** อ่านรายการกฎ */
  | 'rules'
  /** ตั้งกฎใหม่ */
  | 'create'
  /** แก้กฎข้อเดิม */
  | 'edit';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

/**
 * ประโยค "ไม่มีอะไรเปลี่ยน" ของแต่ละ context ที่ **เขียน** — สตริงว่างสำหรับ context
 * ที่อ่านอย่างเดียว
 *
 * ทุก write ของกลุ่มนี้เขียน `audit_log` (entity_type `ProductSupplierQualityRule`)
 * **ในธุรกรรมเดียวกับการเปลี่ยนข้อมูล** ล้มจุดใดก็ไม่เหลือทั้งสองอย่าง — ประโยคนี้จึง
 * เป็นข้อเท็จจริงของ contract ไม่ใช่คำปลอบใจ
 */
function nothingChangedText(context: QualityFailureContext): string {
  switch (context) {
    case 'create':
      return ' ไม่มีกฎข้อใดถูกสร้างและไม่มีแถว audit ถูกเขียน';
    case 'edit':
      return ' กฎข้อนี้ยังเป็นชุดเดิมทุกฟิลด์และไม่มีแถว audit ถูกเขียน';
    case 'rules':
      return '';
  }
}

function isWriteContext(context: QualityFailureContext): boolean {
  return nothingChangedText(context) !== '';
}

/**
 * ข้อความของกฎแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ contract เขียนไว้
 *
 * สามข้อแรกต้อง **อ่านรู้เรื่องสำหรับคนที่ไม่ใช่โปรแกรมเมอร์** เพราะทั้งสามเป็น
 * สิ่งที่ผู้ใช้แก้เองได้ทันที: คู่ที่ซ้ำแก้ที่รายการข้างล่าง ส่วนรหัสที่ไม่มีในระบบ
 * แก้ที่ช่องที่พิมพ์ผิด — และ `422` สองตัวมาด้วยรหัสเดียวกัน ต่างกันแค่ `details.rule`
 * ซึ่งเป็นตัวเดียวที่บอกว่าเป็นช่องไหน
 *
 * ⚠️ ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุลงใน `switch` นี้ — รหัสที่หน้าจอคิดขึ้นเอง
 * คือข้อความที่ไม่มีวันแสดง และเป็นคำสัญญาที่ backend ไม่เคยให้ไว้
 */
function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case QUALITY_RULE_EXISTS:
      return {
        heading: 'คู่สินค้ากับผู้ขายคู่นี้มีกฎอยู่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${QUALITY_RULE_EXISTS} — หนึ่งคู่มีกฎได้ข้อเดียว และกฎของคู่นี้ถูกตั้งไว้ก่อนหน้านี้แล้ว ถ้าต้องการเปลี่ยนคำตอบให้กด "แก้ไข" ที่แถวของคู่นี้ในรายการด้านล่าง แทนการตั้งกฎใหม่ทับ **ไม่มีกฎข้อใดถูกสร้างขึ้น และกฎเดิมของคู่นี้ไม่ถูกทับ**`,
      };
    case PRODUCT_NOT_FOUND:
      return {
        heading: 'ไม่พบรหัสสินค้านี้ในระบบ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_NOT_FOUND} — รหัสสินค้าที่กรอกไม่ตรงกับสินค้ารายการใดในทะเบียนสินค้า ตรวจตัวสะกดอีกครั้ง (ช่องว่างหน้า/หลังถูกตัดให้แล้ว) หรือให้ผู้ดูแลข้อมูลหลักเพิ่มสินค้ารายการนี้ก่อน **รหัสผู้ขายที่กรอกไว้ไม่ใช่ปัญหาของคำตอบนี้**`,
      };
    case SUPPLIER_NOT_FOUND:
      return {
        heading: 'ไม่พบรหัสผู้ขายนี้ในระบบ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SUPPLIER_NOT_FOUND} — รหัสผู้ขายที่กรอกไม่ตรงกับผู้ขายรายใดในทะเบียนผู้ขาย ตรวจตัวสะกดอีกครั้ง หรือเพิ่มผู้ขายรายนี้ที่หน้า "ผู้ขาย (Supplier Master)" ก่อน **รหัสสินค้าที่กรอกไว้ไม่ใช่ปัญหาของคำตอบนี้**`,
      };
    case QUALITY_RULE_PAIR_IS_IMMUTABLE:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — คู่สินค้า/ผู้ขายถูกส่งไปกับคำขอแก้ไข',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${QUALITY_RULE_PAIR_IS_IMMUTABLE} — คู่สินค้ากับผู้ขายคือสิ่งที่ระบุว่านี่คือกฎข้อไหน การย้ายกฎไปอีกคู่หนึ่งจะเปลี่ยนคำตอบของสองคู่พร้อมกัน จึงแก้ไม่ได้เลย กล่องแก้ไขนี้ไม่มีเส้นทางที่ส่งคู่นี้ไปกับคำขอด้วยซ้ำ การที่มันไปถึงเซิร์ฟเวอร์จึงเป็นบั๊ก — แจ้งผู้ดูแลระบบ อย่าลองกดซ้ำ **กฎข้อนี้ยังเป็นชุดเดิมทุกฟิลด์**`,
      };
    case QUALITY_RULE_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — ฟิลด์ของเซิร์ฟเวอร์ถูกส่งไปด้วย',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${QUALITY_RULE_FIELD_IS_SERVER_OWNED} — รหัสภายใน (rule_id, product_id, supplier_id) ผู้สร้าง และเวลาที่สร้าง/แก้ไขเป็นของเซิร์ฟเวอร์ ไม่มีฟอร์มใดในหน้าจอนี้กรอกมันได้ การที่มันไปถึงเซิร์ฟเวอร์จึงเป็นบั๊ก — แจ้งผู้ดูแลระบบ`,
      };
    default:
      return null;
  }
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็น
 * ผู้พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(
  failure: QualityRuleFailure,
  context: QualityFailureContext,
): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return {
        heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
        text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${INVENTORY_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่ กฎตรวจรับเข้าเป็นกฎระดับองค์กรและไม่มีขอบเขตโรงงาน (D-086) การถูกปฏิเสธจึงไม่ใช่เรื่องของโรงงานที่คุณสังกัด — ขอสิทธิ์เพิ่มที่ตารางสิทธิ์ตามแผนก${nothingChanged}`,
      };
    case 'not-found':
      return {
        heading: 'ไม่พบกฎข้อที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — กฎข้อนี้ไม่มีอยู่แล้ว (อาจถูกแก้โดยผู้ใช้อีกคนระหว่างที่หน้าจอนี้เปิดค้างอยู่) โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'มีของที่ชนกันอยู่แล้วในระบบ',
          text: `เซิร์ฟเวอร์ตอบ 409 — สิ่งที่ส่งไปชนกับสิ่งที่มีอยู่แล้ว ไม่ใช่ข้อผิดพลาดของระบบและการกดซ้ำจะได้คำตอบเดิม${nothingChanged}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น หมายเหตุยาวเกิน ${QUALITY_RULE_NOTE_MAX_LENGTH} ตัวอักษร หรือรหัสยาวเกินความยาวที่คอลัมน์รับได้)${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และ **ไม่มีอะไรยืนยันว่ากฎถูกเปลี่ยนหรือไม่** — โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง และจะไม่เดาขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงรายการกฎที่อ่านได้ไม่ครบ — กฎที่ขาดไปหนึ่งข้ออ่านเหมือน "คู่นี้ยังไม่มีกฎ" ซึ่งแปลว่าของที่รับเข้าจะถูกกักไว้ตามค่าเริ่มต้น และจะพาไปสู่การตั้งกฎซ้ำที่เซิร์ฟเวอร์ปฏิเสธ',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

function envelopeOf(failure: QualityRuleFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

interface QualityRuleFailureNoticeProps {
  readonly failure: QualityRuleFailure;
  readonly context: QualityFailureContext;
  readonly onRetry?: () => void;
}

export function QualityRuleFailureNotice({
  failure,
  context,
  onRetry,
}: QualityRuleFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  // `409` และ `422` เป็นคำตอบที่ **ผู้ใช้แก้เองได้** (ไปแก้กฎเดิม / แก้รหัสที่พิมพ์ผิด)
  // ต่างจากความล้มเหลวที่เหลือซึ่งเขาทำอะไรไม่ได้เลย — สีของป้ายจึงบอกเรื่องนั้น
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="quality-rule-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="quality-rule-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="quality-rule-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface IncomingInspectionRuleScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type RulesState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly ProductSupplierQualityRule[] }
  | QualityRuleFailure;

/** คำขอที่กำลังเดินทางอยู่ — ใช้ปิดปุ่มทั้งหน้าจอระหว่างนั้น */
type PendingKind = 'create-rule' | 'update-rule';

export function IncomingInspectionRuleScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: IncomingInspectionRuleScreenProps) {
  const canCreate = can(permissions, INVENTORY_MODULE, 'CREATE');
  const canEdit = can(permissions, INVENTORY_MODULE, 'EDIT');

  const [rules, setRules] = useState<RulesState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<PendingKind | null>(null);

  const [query, setQuery] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createProduct, setCreateProduct] = useState('');
  const [createSupplier, setCreateSupplier] = useState('');
  /** **ว่างเปล่าโดยตั้งใจ** — ธงนี้ไม่มีค่าเริ่มต้นบนสาย และไม่มีที่นี่ด้วย (D-086) */
  const [createInspection, setCreateInspection] = useState<string>(INSPECTION_UNDECIDED);
  const [createNote, setCreateNote] = useState('');
  const [createFailure, setCreateFailure] = useState<QualityRuleFailure | null>(null);
  const [createdRule, setCreatedRule] = useState<ProductSupplierQualityRule | null>(null);

  /** กฎที่กล่องแก้ไขกำลังเปิดค้างอยู่ — `null` คือไม่มีกล่องเปิด */
  const [editing, setEditing] = useState<ProductSupplierQualityRule | null>(null);
  const [editInspection, setEditInspection] = useState<string>(INSPECTION_UNDECIDED);
  const [editNote, setEditNote] = useState('');
  const [editFailure, setEditFailure] = useState<QualityRuleFailure | null>(null);
  const [updatedRule, setUpdatedRule] = useState<ProductSupplierQualityRule | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // ระหว่างโหลดซ้ำหลังบันทึกสำเร็จ **ตารางเดิมยังอยู่** ไม่กระพริบเป็นหน้าว่าง:
    // ผู้ใช้เพิ่งกดปุ่มไป สิ่งที่เขาต้องเห็นคือผลของมัน ไม่ใช่หน้าจอที่หายไปทั้งจอ
    setRules((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchQualityRules({}, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setRules(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    // เปิดกล่องใหม่ = เริ่มใหม่: ผลของครั้งก่อน (สำเร็จหรือถูกปฏิเสธ) ไม่ใช่ผลของครั้งนี้
    setCreateProduct('');
    setCreateSupplier('');
    setCreateInspection(INSPECTION_UNDECIDED);
    setCreateNote('');
    setCreateFailure(null);
    setCreatedRule(null);
    setCreateOpen(true);
  };

  /**
   * อ้างอิงคงที่ — `Modal` ผูก/ถอน listener ของ Esc ตาม `onClose` ถ้าฟังก์ชันเปลี่ยน
   * ตัวทุกครั้งที่ผู้ใช้พิมพ์ listener จะถูกผูกใหม่ทุกตัวอักษรโดยไม่จำเป็น
   */
  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const openEdit = (rule: ProductSupplierQualityRule) => {
    if (!canEdit) {
      return;
    }
    // ค่าเริ่มต้นของฟอร์มคือ **ค่าที่เซิร์ฟเวอร์ส่งมาล่าสุด** ไม่ใช่ค่าว่าง — กล่องนี้
    // แก้ของที่มีอยู่ ไม่ได้สร้างใหม่ และ `toUpdateQualityRuleBody` จะไม่ส่งฟิลด์ที่
    // ผู้ใช้ไม่ได้แตะไปกับคำขอ
    setEditInspection(flagToInspectionChoice(rule.incoming_inspection_required));
    setEditNote(rule.note ?? '');
    setEditFailure(null);
    setUpdatedRule(null);
    setEditing(rule);
  };

  const closeEdit = useCallback(() => {
    setEditing(null);
  }, []);

  const submitCreate = async () => {
    const body = toCreateQualityRuleBody(
      createProduct,
      createSupplier,
      createInspection,
      createNote,
    );
    if (body === null || pending !== null || !canCreate) {
      return;
    }

    setPending('create-rule');
    setCreateFailure(null);
    setCreatedRule(null);

    const result = await createQualityRule(body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย และไม่ปิดกล่อง** — สิ่งที่ผู้ใช้เห็นยังเป็นสถานะที่เซิร์ฟเวอร์
      // ยืนยันล่าสุด และสิ่งที่เขาพิมพ์ไว้ยังอยู่ในช่องให้แก้แล้วส่งใหม่ได้ทันที ซึ่ง
      // สำหรับ `PRODUCT_NOT_FOUND`/`SUPPLIER_NOT_FOUND` คือสิ่งเดียวที่เขาต้องทำ
      setCreateFailure(result);
      return;
    }

    setCreatedRule(result.rule);
    setCreateOpen(false);
    // คำค้นที่ค้างอยู่ถูกล้างทิ้ง ไม่งั้นกฎที่เพิ่งตั้ง (ซึ่งมักไม่ตรงกับคำค้นเดิม) จะถูก
    // กรองออกทันทีที่โหลดเสร็จ และผู้ใช้จะเห็นคำว่า "ตั้งกฎแล้ว" คู่กับรายการที่ไม่มี
    // กฎข้อนั้นอยู่เลย
    setQuery('');
    setReloadToken((token) => token + 1);
  };

  const submitEdit = async () => {
    const target = editing;
    if (target === null || pending !== null || !canEdit) {
      return;
    }
    const body = toUpdateQualityRuleBody(target, editInspection, editNote);
    if (body === null) {
      return;
    }

    setPending('update-rule');
    setEditFailure(null);
    setUpdatedRule(null);

    const result = await updateQualityRule(target.rule_id, body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setEditFailure(result);
      return;
    }

    setUpdatedRule(result.rule);
    setEditing(null);
    // คำค้นไม่ถูกล้างที่นี่ ต่างจากการสร้าง: แถวที่เพิ่งแก้มาจากรายการที่ผู้ใช้กำลัง
    // มองอยู่ การล้างคำค้นจะพาเขาออกจากตำแหน่งที่เขาทำงานอยู่โดยไม่ได้ขอ
    setReloadToken((token) => token + 1);
  };

  const ruleItems = rules.status === 'ready' ? rules.items : [];
  const matchedItems = filterQualityRules(ruleItems, query);
  const busy = pending !== null;
  const createBody = toCreateQualityRuleBody(
    createProduct,
    createSupplier,
    createInspection,
    createNote,
  );
  const editBody =
    editing === null ? null : toUpdateQualityRuleBody(editing, editInspection, editNote);

  return (
    <div className="cap-page" data-testid="incoming-inspection-rules">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            กฎตรวจรับเข้า (Incoming Inspection Rule)
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  ตั้งว่าของที่รับเข้าของแต่ละคู่ <strong>สินค้า + ผู้ขาย</strong>{' '}
                  ต้องกักรอตรวจหรือไม่ — เป็นกฎระดับองค์กร{' '}
                  <strong>ไม่แยกตามโรงงาน</strong> (D-086){' '}
                  <strong>คู่ที่ยังไม่มีกฎถือว่าต้องตรวจเสมอ</strong> ซึ่งเป็นค่าเริ่มต้นฝั่ง
                  ปลอดภัย (D-059)
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canCreate && !canEdit ? (
        <div className="cap-notice" data-testid="quality-rule-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังตั้งหรือแก้กฎตรวจรับเข้าไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag{' '}
            <code className="cap-table__code">can_create</code> หรือ{' '}
            <code className="cap-table__code">can_edit</code> ของโมดูล{' '}
            <code className="cap-table__code">{INVENTORY_MODULE}</code> ปุ่ม
            &quot;ตั้งกฎใหม่&quot; และ &quot;แก้ไข&quot; จึงไม่แสดง — รายการและช่องค้นหา
            ยังใช้ได้ตามปกติ
          </p>
          <p className="cap-notice__note">
            การซ่อนปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ
            backend ทุกคำขอเสมอ
          </p>
        </div>
      ) : null}

      {/* ------------------------------------------------- ค้นหา + ปุ่มสร้าง -- */}

      <div className="cap-filters" data-testid="quality-rule-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">ค้นหากฎ</span>
          <input
            className="cap-input"
            type="search"
            name="quality_rule_query"
            data-testid="quality-rule-search"
            value={query}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          <InfoHint
            label="เกี่ยวกับช่องค้นหา"
            text="กรองรายการด้านล่างจากรหัสสินค้าหรือรหัสผู้ขาย — กรองจากรายการที่โหลดมาแล้ว
            ไม่ได้ถามเซิร์ฟเวอร์ใหม่ ช่องว่างหมายถึงแสดงทุกข้อ"
          />
        </label>

        {canCreate ? (
          <button
            type="button"
            className="cap-button cap-button--primary"
            data-testid="quality-rule-create-open"
            onClick={openCreate}
          >
            ตั้งกฎใหม่
          </button>
        ) : null}
      </div>

      {/* --------------------------------------------------------- สร้างใหม่ -- */}

      <Modal open={canCreate && createOpen} onClose={closeCreate} title="ตั้งกฎตรวจรับเข้าใหม่">
        <form
          className="cap-form"
          data-testid="quality-rule-create-form"
          aria-label="ตั้งกฎตรวจรับเข้าใหม่"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสสินค้า</span>
            <input
              className="cap-input"
              name="product_code"
              data-testid="quality-rule-create-product"
              value={createProduct}
              autoComplete="off"
              onChange={(event) => {
                setCreateProduct(event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับรหัสสินค้า"
              text={
                <>
                  ต้องเป็นรหัสที่มีอยู่จริงในทะเบียนสินค้า — รหัสที่ไม่มีจะถูกปฏิเสธพร้อมกฎ{' '}
                  <code className="cap-table__code">{PRODUCT_NOT_FOUND}</code>
                </>
              }
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">รหัสผู้ขาย</span>
            <input
              className="cap-input"
              name="supplier_code"
              data-testid="quality-rule-create-supplier"
              value={createSupplier}
              autoComplete="off"
              onChange={(event) => {
                setCreateSupplier(event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับรหัสผู้ขาย"
              text={
                <>
                  ต้องเป็นรหัสที่มีอยู่จริงในทะเบียนผู้ขาย — รหัสที่ไม่มีจะถูกปฏิเสธพร้อมกฎ{' '}
                  <code className="cap-table__code">{SUPPLIER_NOT_FOUND}</code>
                </>
              }
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">ของที่รับเข้าจากคู่นี้</span>
            <select
              className="cap-input"
              name="incoming_inspection_required"
              data-testid="quality-rule-create-inspection"
              value={createInspection}
              onChange={(event) => {
                setCreateInspection(event.target.value);
              }}
            >
              {/**
               * ตัวเลือกแรกไม่ใช่ "ใช้ค่าเริ่มต้นของเซิร์ฟเวอร์" แบบช่องสถานะของหน้า
               * ผู้ขาย — มันคือ **"ยังไม่ได้เลือก"** ซึ่งส่งไม่ได้ ธงนี้ไม่มี default
               * บนสายโดยตั้งใจ (D-086) ปุ่มบันทึกจึงกดไม่ได้ตราบที่ค่านี้ยังถูกเลือกอยู่
               */}
              <option value={INSPECTION_UNDECIDED}>— ยังไม่ได้เลือก —</option>
              {INSPECTION_CHOICE_DESCRIPTORS.map((descriptor) => (
                <option key={descriptor.value} value={descriptor.value}>
                  {descriptor.label}
                </option>
              ))}
            </select>
            <InfoHint
              label="เกี่ยวกับช่องนี้"
              text={
                <>
                  <strong>ต้องเลือก</strong> — ระบบถือว่าคู่ที่ไม่มีกฎต้องตรวจอยู่แล้ว
                  กฎที่ไม่ได้ระบุคำตอบจึงไม่ได้บอกอะไรเพิ่ม{' '}
                  {INSPECTION_CHOICE_DESCRIPTORS.map(
                    (descriptor) => `${descriptor.label}: ${descriptor.summary}`,
                  ).join(' / ')}
                </>
              }
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">หมายเหตุ (ไม่บังคับ)</span>
            <input
              className="cap-input"
              name="note"
              data-testid="quality-rule-create-note"
              value={createNote}
              maxLength={QUALITY_RULE_NOTE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateNote(event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับหมายเหตุ"
              text={`ยาวไม่เกิน ${QUALITY_RULE_NOTE_MAX_LENGTH} ตัวอักษร — เว้นว่างได้ และแก้ทีหลังได้`}
            />
          </label>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="quality-rule-create-submit"
              disabled={busy || createBody === null}
            >
              {pending === 'create-rule' ? 'กำลังตั้งกฎ…' : 'ตั้งกฎ'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="quality-rule-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          {/**
           * ความล้มเหลวอยู่ **ในกล่อง** ข้างฟอร์มที่ทำให้มันเกิด — ผู้ใช้แก้รหัสแล้ว
           * กดส่งใหม่ได้ทันทีโดยไม่ต้องเปิดกล่องใหม่และพิมพ์ใหม่ทั้งหมด
           */}
          {createFailure === null ? null : (
            <QualityRuleFailureNotice failure={createFailure} context="create" />
          )}
        </form>
      </Modal>

      {createdRule === null ? null : (
        <div className="cap-notice" data-testid="quality-rule-create-result" role="status">
          <StatusBadge tone="success">ตั้งกฎแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            ตั้งกฎของ {createdRule.product_code} กับ {createdRule.supplier_code} แล้ว
          </h2>
          <p className="cap-notice__text">
            ของที่รับเข้าจากคู่นี้:{' '}
            <strong>
              {incomingInspectionLabel(createdRule.incoming_inspection_required)}
            </strong>{' '}
            — <strong>คู่สินค้ากับผู้ขายแก้ไม่ได้อีกแล้ว</strong>{' '}
            ส่วนคำตอบและหมายเหตุแก้ได้จากปุ่ม &quot;แก้ไข&quot; ในรายการด้านล่าง
          </p>
        </div>
      )}

      {/* ---------------------------------------------------------- แก้ไข --- */}

      <Modal open={canEdit && editing !== null} onClose={closeEdit} title="แก้ไขกฎตรวจรับเข้า">
        {editing === null ? null : (
          <form
            className="cap-form"
            data-testid="quality-rule-edit-form"
            aria-label="แก้ไขกฎตรวจรับเข้า"
            onSubmit={(event) => {
              event.preventDefault();
              void submitEdit();
            }}
          >
            {/**
             * คู่สินค้า/ผู้ขายเป็น **ข้อความ ไม่ใช่ช่องกรอกที่ disabled** โดยตั้งใจ:
             * ช่องที่กรอกไม่ได้ยังอ่านเหมือน "ช่องที่จะปลดล็อกได้สักวัน" ส่วนข้อความ
             * อ่านว่าเป็นข้อเท็จจริงของ record นี้ และไม่มี input ตัวไหนที่ค่าของมัน
             * จะหลุดไปกับคำขอได้เลย
             */}
            <div className="cap-field">
              <span className="cap-field__label">
                คู่สินค้าและผู้ขาย
                <InfoHint
                  label="เกี่ยวกับคู่สินค้าและผู้ขาย"
                  text={
                    <>
                      <strong>แก้ไม่ได้</strong> — คู่นี้คือสิ่งที่ระบุว่านี่คือกฎข้อไหน
                      การย้ายกฎไปอีกคู่หนึ่งจะเปลี่ยนคำตอบของสองคู่พร้อมกัน (D-086)
                      คำขอนี้จึงไม่ส่งคู่นี้ไปด้วยเลย
                    </>
                  }
                />
              </span>
              <p data-testid="quality-rule-edit-pair">
                <code className="cap-table__code">{editing.product_code}</code>
                {' + '}
                <code className="cap-table__code">{editing.supplier_code}</code>
              </p>
            </div>

            <label className="cap-field">
              <span className="cap-field__label">ของที่รับเข้าจากคู่นี้</span>
              <select
                className="cap-input"
                name="incoming_inspection_required"
                data-testid="quality-rule-edit-inspection"
                value={editInspection}
                onChange={(event) => {
                  setEditInspection(event.target.value);
                }}
              >
                {INSPECTION_CHOICE_DESCRIPTORS.map((descriptor) => (
                  <option key={descriptor.value} value={descriptor.value}>
                    {descriptor.label}
                  </option>
                ))}
              </select>
              <InfoHint
                label="เกี่ยวกับช่องนี้"
                text={
                  <>
                    ยกเลิกการตรวจของคู่นี้คือการเปลี่ยนคำตอบเป็น &quot;ไม่ต้องตรวจ&quot; —{' '}
                    <strong>ไม่มีการลบกฎ</strong> เพราะการลบแถวทิ้งจะทำให้ระบบกลับไปกักของ
                    ตามค่าเริ่มต้นโดยไม่มีใครรู้ว่าใครเป็นคนเปลี่ยน
                  </>
                }
              />
            </label>

            <label className="cap-field">
              <span className="cap-field__label">หมายเหตุ</span>
              <input
                className="cap-input"
                name="note"
                data-testid="quality-rule-edit-note"
                value={editNote}
                maxLength={QUALITY_RULE_NOTE_MAX_LENGTH}
                autoComplete="off"
                onChange={(event) => {
                  setEditNote(event.target.value);
                }}
              />
              <InfoHint
                label="เกี่ยวกับหมายเหตุ"
                text={
                  <>
                    ลบข้อความจนหมดแล้วบันทึก = ล้างหมายเหตุ (ส่ง{' '}
                    <code className="cap-table__code">null</code> ไปจริง ๆ)
                  </>
                }
              />
            </label>

            <div className="cap-form__actions">
              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="quality-rule-edit-submit"
                disabled={busy || editBody === null}
              >
                {pending === 'update-rule' ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข'}
              </button>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="quality-rule-edit-cancel"
                disabled={busy}
                onClick={closeEdit}
              >
                ยกเลิก
              </button>
            </div>

            {editFailure === null ? null : (
              <QualityRuleFailureNotice failure={editFailure} context="edit" />
            )}
          </form>
        )}
      </Modal>

      {updatedRule === null ? null : (
        <div className="cap-notice" data-testid="quality-rule-edit-result" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            บันทึกกฎของ {updatedRule.product_code} กับ {updatedRule.supplier_code} แล้ว
          </h2>
          <p className="cap-notice__text">
            ของที่รับเข้าจากคู่นี้ตอนนี้:{' '}
            <strong>{incomingInspectionLabel(updatedRule.incoming_inspection_required)}</strong>{' '}
            หมายเหตุ: {updatedRule.note ?? '—'} — ค่าเหล่านี้มาจากคำตอบของเซิร์ฟเวอร์
            ไม่ใช่จากสิ่งที่หน้าจอเพิ่งส่งไป
          </p>
        </div>
      )}

      {/* --------------------------------------------------------- รายการกฎ -- */}

      {rules.status === 'loading' ? (
        <div className="cap-notice" data-testid="quality-rule-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการกฎตรวจรับเข้า…</p>
        </div>
      ) : null}

      {rules.status !== 'loading' && rules.status !== 'ready' ? (
        <QualityRuleFailureNotice
          failure={rules}
          context="rules"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {rules.status === 'ready' && ruleItems.length === 0 ? (
        <div className="cap-notice" data-testid="quality-rule-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีกฎ</StatusBadge>
          <h2 className="cap-notice__title">ยังไม่มีกฎตรวจรับเข้าข้อใดในระบบ</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว และ{' '}
            <strong>ไม่ได้แปลว่าของที่รับเข้าไม่ถูกตรวจ</strong>: คู่ที่ยังไม่มีกฎถือว่า
            ต้องกักรอตรวจอยู่แล้วตามค่าเริ่มต้น (D-059)
          </p>
        </div>
      ) : null}

      {/**
       * "หาไม่เจอ" ไม่ใช่ "ยังไม่มีกฎ" และไม่ใช่ "โหลดไม่สำเร็จ"
       *
       * ทั้งสามอย่างให้หน้าจอที่ไม่มีตารางเหมือนกัน แต่สิ่งที่ผู้ใช้ต้องทำต่อคนละเรื่อง
       * กันโดยสิ้นเชิง — อันนี้แก้ด้วยการลบคำค้น ไม่ใช่การตั้งกฎใหม่หรือโทรหาไอที
       */}
      {rules.status === 'ready' && ruleItems.length > 0 && matchedItems.length === 0 ? (
        <div className="cap-notice" data-testid="quality-rule-no-match" role="status">
          <StatusBadge tone="neutral">ไม่พบกฎที่ค้นหา</StatusBadge>
          <h2 className="cap-notice__title">
            ไม่มีกฎที่ตรงกับ &quot;{query.trim()}&quot;
          </h2>
          <p className="cap-notice__text">
            รายการนี้มีกฎ {ruleItems.length} ข้อ แต่ไม่มีข้อใดที่รหัสสินค้าหรือรหัสผู้ขาย
            มีคำนี้อยู่ — ล้างคำค้นเพื่อดูทั้งหมด
          </p>
        </div>
      ) : null}

      {rules.status === 'ready' && matchedItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="กฎตรวจรับเข้าทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">กฎตรวจรับเข้าทั้งหมด</h2>
            <span className="cap-card__hint">
              {query.trim() === ''
                ? 'เรียงตามรหัสสินค้าแล้วรหัสผู้ขายตามที่เซิร์ฟเวอร์ส่งมา'
                : `แสดง ${matchedItems.length} จาก ${ruleItems.length} ข้อที่ตรงกับคำค้น`}
            </span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="quality-rule-table">
              <caption className="cap-visually-hidden">
                กฎตรวจรับเข้าของทุกคู่สินค้า+ผู้ขาย พร้อมคำตอบว่าของที่รับเข้าต้องกักรอ
                ตรวจหรือไม่ และผู้ที่ตั้งกฎข้อนั้นไว้
              </caption>
              <thead>
                <tr>
                  <th scope="col">รหัสสินค้า</th>
                  <th scope="col">รหัสผู้ขาย</th>
                  <th scope="col">ของที่รับเข้า</th>
                  <th scope="col">หมายเหตุ</th>
                  <th scope="col">ผู้ตั้งกฎ</th>
                  {canEdit ? <th scope="col">การแก้ไข</th> : null}
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((rule) => (
                  <tr key={rule.rule_id} data-testid={`quality-rule-row-${rule.rule_id}`}>
                    <th scope="row" className="cap-table__row-head">
                      <code className="cap-table__code">{rule.product_code}</code>
                    </th>
                    <td>
                      <code className="cap-table__code">{rule.supplier_code}</code>
                    </td>
                    <td>
                      {/* ข้อความที่อ่านออก ไม่ใช่ `true`/`false` ดิบ ๆ */}
                      <StatusBadge
                        tone={incomingInspectionTone(rule.incoming_inspection_required)}
                      >
                        {incomingInspectionLabel(rule.incoming_inspection_required)}
                      </StatusBadge>
                    </td>
                    <td>{rule.note ?? '—'}</td>
                    <td>
                      <code className="cap-table__code">{rule.created_by_employee_id}</code>
                    </td>
                    {canEdit ? (
                      <td>
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`quality-rule-edit-open-${rule.rule_id}`}
                          onClick={() => {
                            openEdit(rule);
                          }}
                        >
                          แก้ไข
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
